// ============================================================================
// GeoMessageComposer.tsx — «Отправить геометку» (ЧЕРНОВИК, честное ограничение).
//
// Серверный тип сообщения «geo» НЕ поддержан, поэтому геометка передаётся
// ОБЫЧНЫМ сообщением с каноническим текстом (lib/geo/geoMessage.formatGeoMessage)
// через существующий механизм отправки (chatQueue.sendChatMessage). Превью карты
// рисуется локально (GeoMessagePreview). Серверных полей НЕ добавлялось.
// ============================================================================

import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { GeoLocateButton } from './GeoLocateButton';
import { MapView, type MapViewHandle, type GeoMarker } from './MapView';
import type { GeoMessagePoint } from '@/lib/geo/geoMessage';

interface GeoMessageComposerProps {
  onClose: () => void;
  onSend: (point: GeoMessagePoint) => void | Promise<void>;
  sending?: boolean;
}

export function GeoMessageComposer({ onClose, onSend, sending }: GeoMessageComposerProps) {
  const mapRef = useRef<MapViewHandle | null>(null);
  const [point, setPoint] = useState<GeoMessagePoint | null>(null);
  const [label, setLabel] = useState('');

  const pick = (lat: number, lon: number) => setPoint({ lat, lon, label });

  const markers: GeoMarker[] = point
    ? [{ id: '__pick', lat: point.lat, lon: point.lon, label: label || 'Геометка', kind: 'selected' }]
    : [];

  const send = () => {
    if (!point) return;
    void onSend({ lat: point.lat, lon: point.lon, label });
  };

  return (
    <UniversalWindow open onClose={onClose} title="Отправить геометку" blocking width="max-w-2xl" resizable={false}>
      <div className="p-2 space-y-2">
        <div className="text-[11px] text-muted-foreground">
          Нажмите на карту, чтобы поставить точку. Кнопка «Где я» определяет ваше местоположение.
        </div>
        <MapView
          ref={mapRef}
          markers={markers}
          height={340}
          autoFit={false}
          showLocate={false}
          onMapClick={pick}
        />
        <div className="flex items-center gap-2">
          <GeoLocateButton
            onLocated={(p) => {
              setPoint({ lat: p.lat, lon: p.lon, label });
              mapRef.current?.recenter(p.lat, p.lon, 15);
            }}
          />
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Подпись места (необязательно)"
            className="h-8 text-[13px]"
          />
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[11px] text-muted-foreground font-mono">
            {point ? `${point.lat.toFixed(6)}, ${point.lon.toFixed(6)}` : 'Точка не выбрана'}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={onClose} disabled={sending}>
              Отмена
            </Button>
            <Button size="sm" className="h-8 text-[12px]" onClick={send} disabled={!point || sending}>
              {sending ? 'Отправка…' : 'Отправить геометку'}
            </Button>
          </div>
        </div>
        <Label className="text-[10px] text-muted-foreground block">
          Черновик: геометка отправляется текстом сообщения (серверный тип «гео» пока не поддержан) и
          отображается превью-картой в ленте.
        </Label>
      </div>
    </UniversalWindow>
  );
}
